<template>
  <div>
    <div v-if="hasUserTraits" class="input-insert-button">
      <a class="cursor-pointer" @click="insertVariableMsg">
        <v-icon start size="14" color="accent">md:add</v-icon>
        <span>Insert variable</span>
      </a>
    </div>
    <v-dialog v-model="newDynamicValueDialog" width="550" maxWidth="550">
      <v-card>
        <v-card-title class="headline">{{ modalVariableTitle }}</v-card-title>
        <v-card-text>
          <RfSelect
            v-model="selectedVariableGroup"
            :items="[
              { text: 'Variables from data sources', value: 'data-sources' },
              { text: 'User variables', value: 'dynamic' },
            ]"
            itemTitle="text"
            title="Group"
            :hideDetails="!selectedVariableGroup"
            variant="outlined"
            density="compact"
            placeholder="Select group"
          />
          <RfSelect
            v-if="selectedVariableGroup === 'dynamic'"
            v-model="selectedDynamicTrait"
            :items="availableUserTraits"
            title="User Variable"
            variant="outlined"
            itemTitle="text"
            hideDetails
            density="compact"
            placeholder="Select variable"
          />
          <template v-if="selectedVariableGroup === 'data-sources'">
            <RfSelect
              v-model="selectedDataSource.value"
              :items="dataSourcesList"
              itemTitle="text"
              title="Source"
              variant="outlined"
              density="compact"
              placeholder="Select source"
              @update:modelValue="
                selectedDataSource.value && (selectedDataSource.item = selectedDynamicTrait = null)
              "
            />
            <RfSelect
              v-model="selectedDataSource.item"
              :items="dataSourcesItemsList"
              itemTitle="text"
              title="Item"
              variant="outlined"
              density="compact"
              placeholder="Select item"
              @update:modelValue="selectedDataSource.item && (selectedDynamicTrait = null)"
            />
            <RfSelect
              v-model="selectedDynamicTrait"
              :items="dataSourceItemDataList"
              itemTitle="text"
              title="Variable"
              variant="outlined"
              hideDetails
              density="compact"
              placeholder="Select variable"
            />
          </template>
        </v-card-text>
        <v-card-actions class="modal-card-actions-div">
          <RfButton
            buttonText="Cancel"
            class="cancel-btn px-5!"
            variant="outlined"
            @click="closeInsertModal"
          />
          <RfButton
            color="primary"
            buttonText="Insert variable"
            class="px-7!"
            :disabled="!selectedDynamicTrait || !selectedVariableGroup"
            @click="addDynamicTrait"
          />
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import RfSelect from "@/components/legacy/RfSelectLegacy.vue";
import RfButton from "@/components/buttons/RfButton.vue";
import { useDataSourcesStore } from "@/pinia/dataSourcesStore";
import { useCustomFields } from "@/utils/composables/useCustomFields";

const modes = {
  LIQUID: "liquid",
  default: "default",
  MIXED: "mixed",
};
export default {
  name: "RfInsertUserTrait",
  components: { RfSelect, RfButton },
  props: {
    app: Object,
    actionName: String,
    title: String,
    actions: Object,
    noSpace: { type: Boolean, default: false },
    positionRef: { type: [Object], default: null },
    mode: {
      type: String,
      default: modes.default,
      validator: value => Object.values(modes).includes(value),
    },
  },
  setup: props => ({
    dataSourcesStore: useDataSourcesStore(),
    customFields: useCustomFields({ app: props.app }),
  }),
  data() {
    return {
      selectedVariableGroup: null,
      selectedDynamicTrait: null,
      selectedDataSource: { value: null, item: null },
      newDynamicValueDialog: false,
      selectionStart: null,
      selectionEnd: null,
    };
  },
  computed: {
    dataSourcesList() {
      if (!this.dataSourcesStore.dataSources) return [];
      return this.dataSourcesStore.dataSources.map(el => ({ text: el.name, value: el }));
    },
    dataSourcesItemsList() {
      if (!this.selectedDataSource.value) return [];
      const displayName = Object.entries(this.selectedDataSource.value.properties).find(
        ([_, v]) => v.use_as_display_name,
      );
      return this.selectedDataSource.value.data_source_items.map(el => ({
        text: this.dataSourceDisplay(el, displayName),
        value: el,
      }));
    },
    dataSourceItemDataList() {
      if (!this.selectedDataSource.item) return [];

      return Object.keys(this.selectedDataSource.item.data).reduce(
        (acc, el) =>
          this.selectedDataSource.value.properties[el].visible
            ? [
                ...acc,
                {
                  text: el,
                  value: `[[${this.selectedDataSource.value.name}.${this.selectedDataSource.item.key}.${el}]]`,
                },
              ]
            : acc,
        [],
      );
    },
    availableUserTraits() {
      if (!this.app) return [];
      return this.customFields.appCustomFields.value.map(({ label }) => ({
        text: label,
        value: `[[user.${label}]]`,
      }));
    },
    hasUserTraits() {
      return this.availableUserTraits && this.availableUserTraits.length > 0;
    },
    modalVariableTitle() {
      return `Insert variable into ${this.title}`;
    },
  },
  watch: {
    newDynamicValueDialog(v) {
      if (v) this.dataSourcesStore.getDataSources(this.$route.params.aid);
    },
  },
  methods: {
    dataSourceDisplay(el, displayName) {
      if (el.data[displayName?.[0]]) {
        return `${el.data[displayName[0]]} (${el.key})`;
      }
      return el.key;
    },
    insertVariableMsg() {
      let el = null;
      if (this.positionRef) {
        if (["input", "textarea"].includes(`${this.positionRef?.tagName}`.toLowerCase())) {
          el = this.positionRef;
        } else {
          el = (this.positionRef.$el || this.positionRef)?.querySelector?.("input,textarea");
        }

        if (el) {
          this.selectionStart = el.selectionStart;
          this.selectionEnd = el.selectionEnd;
        }
      }
      this.newDynamicValueDialog = true;
    },
    addDynamicTrait() {
      let action = this.actions[this.actionName];
      let insertTrait = this.noSpace ? this.selectedDynamicTrait : ` ${this.selectedDynamicTrait} `;
      if (this.selectionStart !== null && this.selectionEnd !== null) {
        action =
          action.substring(0, this.selectionStart) +
          insertTrait +
          action.substring(this.selectionEnd);
      } else {
        action += insertTrait;
      }
      this.actions[this.actionName] = action;
      this.closeInsertModal();
    },
    closeInsertModal() {
      this.newDynamicValueDialog = false;
      this.selectedDataSource.item =
        this.selectedDataSource.variable =
        this.selectedDynamicTrait =
          null;
      this.resetSelection();
    },
    resetSelection() {
      this.selectionStart = null;
      this.selectionEnd = null;
    },
  },
};
</script>
